/**
 * EDUMON Start-Smart Logic V6.0 (Multi-Stack Robustness)
 * - Auto-Add PDF
 * - Virtual Bundle Hardening mit "Batch Processing"
 * - Fix für "Chaos" bei mehreren Sets gleichzeitig
 * - Robust gegen Quantity-Merges
 */

const EDM_SMART_CONFIG = {
  handleA: 'schnapp-zu-mein-erstes-angel-abc', 
  handleB: 'nicht-vergessen-das-such-und-merkspiel', 
  handlePDF: 'edumon-eltern-leitfaden'
};

class EdmStartSmart {
  constructor() {
    this.isProcessing = false;
    this.debounceTimer = null;
    this.init();
    
    document.addEventListener('cart:updated', () => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.handleCartUpdate(), 400); // Etwas höherer Debounce für Sicherheit
    });
  }

  init() {
    setTimeout(() => this.handleCartUpdate(), 600);
  }

  // Hilfsmethode um Cart sicher zu holen
  async getCart() {
    try {
      // Immer frisch fetchen, um sicher zu sein, dass wir den Server-Stand haben
      const res = await fetch(window.Shopify.routes.root + 'cart.js', { cache: 'no-store' });
      return await res.json();
    } catch(e){
      console.error('EDM: getCart failed', e);
      return null;
    }
  }

  async handleCartUpdate() {
    if (this.isProcessing) return;
    
    // 1. Analyse Phase
    const cart = await this.getCart();
    if (!cart || cart.item_count === 0) {
      this.cleanupSession();
      return;
    }

    // Wir sammeln ALLE Items ein
    const itemsA = cart.items.filter(i => i.handle === EDM_SMART_CONFIG.handleA);
    const itemsB = cart.items.filter(i => i.handle === EDM_SMART_CONFIG.handleB);
    const itemPDF = cart.items.find(i => i.handle === EDM_SMART_CONFIG.handlePDF);

    // Zählen, wie viele "ungelockte" (lose) Exemplare wir real haben
    // Ein Item mit Quantity 3 zählt als 3 lose Exemplare (sofern keine Property gesetzt ist)
    let freeStackA = [];
    let freeStackB = [];
    let lockedCount = 0;

    // Helper: Stack aufbauen (Jedes physikalische Stück Produkt bekommt einen Eintrag im Array)
    const buildStack = (items, stackArray) => {
      items.forEach(item => {
        // Ist es schon Teil eines Bundles? (Hat _bundle_id)
        if (item.properties && item.properties['_bundle_id']) {
          lockedCount++; // Nur für Statistik
        } else {
          // Es ist "frei". Füge es so oft zum Stack hinzu, wie die Quantity sagt.
          // Wir speichern uns Key und VariantID für spätere Manipulation
          for (let q = 0; q < item.quantity; q++) {
            stackArray.push({ key: item.key, id: item.variant_id || item.id });
          }
        }
      });
    };

    buildStack(itemsA, freeStackA);
    buildStack(itemsB, freeStackB);

    // Wie viele NEUE Sets können wir bilden?
    // Das Minimum aus freien As und freien Bs
    const newSetsCount = Math.min(freeStackA.length, freeStackB.length);

    // --- LOGIC 0: BUNDLE HARDENING (BATCH PROCESS) ---
    // Wenn wir Paare bilden können, müssen wir SOFORT handeln und splitten.
    if (newSetsCount > 0) {
      this.isProcessing = true;
      console.log(`[EDM Start Smart] ${newSetsCount} neue Sets erkannt. Starte Hardening...`);
      try {
        await this.performBatchHardening(cart, newSetsCount, freeStackA, freeStackB);
        return; // Stop, Cart wird neu geladen
      } catch (e) {
        console.error('Hardening Error', e);
        this.isProcessing = false;
        return;
      }
    }

    // --- LOGIC 1 & 2: PDF HANDLING ---
    // Wir machen weiter, nur wenn alles stabil ist (keine neuen Sets zu bilden)
    // Prüfen: Haben wir ÜBERHAUPT Sets (auch alte)?
    // Ein Set ist "complete", wenn wir gelockte Items haben ODER gerade welche gebildet haben (aber Code läuft ja neu an)
    // Wir zählen einfach, ob es überhaupt eine "Bundle ID" im Cart gibt.
    const hasAnyBundle = cart.items.some(i => i.properties && i.properties['_bundle_id']);
    
    // PDF Logik vereinfacht:
    const hasPDF = !!itemPDF;
    const pdfManuallyRemoved = sessionStorage.getItem('edm_pdf_manually_removed') === 'true';
    
    if (hasAnyBundle && !hasPDF && !pdfManuallyRemoved) {
       this.isProcessing = true;
       await this.addPdf(EDM_SMART_CONFIG.handlePDF);
       // this.showRewardToast(); // DELETED: Meldung entfernt
       this.isProcessing = false;
    }
    else if (!hasAnyBundle && hasPDF && itemPDF.final_price === 0) {
       // Remove PDF
       this.isProcessing = true;
       await this.updateCart({ [itemPDF.key]: 0 });
       sessionStorage.removeItem('edm_pdf_manually_removed');
       this.isProcessing = false;
    }
    
    // Manual Removal Detection
    const hadPDFBefore = sessionStorage.getItem('edm_had_pdf') === 'true';
    if (hasAnyBundle && hadPDFBefore && !hasPDF && !pdfManuallyRemoved) {
      sessionStorage.setItem('edm_pdf_manually_removed', 'true');
    }
    
    if (hasPDF) sessionStorage.setItem('edm_had_pdf', 'true');
    else sessionStorage.removeItem('edm_had_pdf');
    
    if(!hasAnyBundle) {
        this.cleanupSession();
    }
  }

  // --- CORE: BATCH HARDENING ---
  // Diese Funktion nimmt die "freien Stacks", generiert IDs und baut den Warenkorb um.
  async performBatchHardening(cart, count, stackA, stackB) {
    const updates = {}; // Für Reduzierungen
    const adds = [];    // Für neue Bundle-Items

    // Wir brauchen aktuelle Quantities für die updates map, da wir die keys mehrfach antreffen können
    let currentQuantities = {};
    cart.items.forEach(i => currentQuantities[i.key] = i.quantity);

    for (let i = 0; i < count; i++) {
      // Wir nehmen jeweils einen Partner vom Stack
      const itemA = stackA[i];
      const itemB = stackB[i];
      
      // Eindeutige ID für DIESES Paar (Zeit + Index verhindert Kollision)
      const uniqueBundleID = `SET-${Date.now()}-${i}`;
      
      // LOGIK A: Reduziere alte Zeile
      if (currentQuantities[itemA.key] > 0) {
        updates[itemA.key] = currentQuantities[itemA.key] - 1;
        currentQuantities[itemA.key]--; // Internen Zähler updaten für nächsten Loop
      }
      
      // LOGIK B: Reduziere alte Zeile
      if (currentQuantities[itemB.key] > 0) {
        updates[itemB.key] = currentQuantities[itemB.key] - 1;
        currentQuantities[itemB.key]--;
      }

      // LOGIK C: Erstelle neue Zeilen mit ID
      adds.push({
        id: itemA.id,
        quantity: 1,
        properties: { '_bundle_id': uniqueBundleID, '_edm_bundle_lock': 'active' }
      });
      adds.push({
        id: itemB.id,
        quantity: 1,
        properties: { '_bundle_id': uniqueBundleID, '_edm_bundle_lock': 'active' }
      });
    }

    // API CALLS
    // 1. Erst Reduzieren (Update)
    // Filtern: Wir senden nur Updates für Keys, die wir angefasst haben
    const finalUpdates = {};
    for (const key in updates) {
        // Safety: Nicht aus Versehen auf -1 setzen
        finalUpdates[key] = Math.max(0, updates[key]);
    }
    
    if (Object.keys(finalUpdates).length > 0) {
      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ updates: finalUpdates })
      });
    }

    // 2. Dann Hinzufügen (Add)
    if (adds.length > 0) {
      // Add in Batches falls es viele sind (optional, aber Add kann Arrays)
      await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ items: adds })
      });
    }

    // 3. Reload
    if (window.EDUMON && window.EDUMON.refreshSections) {
      await window.EDUMON.refreshSections();
    } else {
      window.location.reload();
    }
  }

  // --- HELPERS ---
  async addPdf(handle) {
    try {
      const p = await (await fetch(window.Shopify.routes.root + `products/${handle}.js`)).json();
      const id = p.variants[0].id;
      const sections = window.EDUMON?.SECTION_IDS || ['cart-drawer', 'main-cart-items'];
      
      await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ 
            items: [{id, quantity:1}],
            sections: sections.join(',')
        })
      });
      // Event feuern für Drawer Refresh
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch(e) { console.error('PDF Add failed', e); }
  }

  async updateCart(updates) {
    await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({ updates })
    });
    // Trigger Refresh
    if (window.EDUMON && window.EDUMON.refreshSections) await window.EDUMON.refreshSections();
    else window.location.reload();
  }

  cleanupSession() {
      sessionStorage.removeItem('edm_bundle_was_complete');
      sessionStorage.removeItem('edm_reward_pending');
      sessionStorage.removeItem('edm_pdf_manually_removed');
      sessionStorage.removeItem('edm_had_pdf');
  }
}

new EdmStartSmart();